/**
 * Browser device fingerprint - "TeamViewer-style" stable hardware-bound
 * identifier, computed from signals that remain constant across browser
 * sessions, cache clears, and incognito mode on the same physical device
 * + browser install.
 *
 * Signals combined:
 *   - navigator.userAgent + platform + hardwareConcurrency + deviceMemory
 *   - navigator.languages (first-language locale)
 *   - screen.width / height / colorDepth / pixelDepth / availWidth / availHeight
 *   - Intl.DateTimeFormat().resolvedOptions().timeZone
 *   - Canvas 2D rendering signature (font metrics + drawing primitive output)
 *   - WebGL GPU vendor + renderer (UNMASKED_VENDOR_WEBGL / UNMASKED_RENDERER_WEBGL)
 *   - Audio context render signature (sample-rate + a short tone hash)
 *
 * Hashed with SHA-256, base64url-encoded. Approximately 256 bits of
 * entropy across users, deterministic per-device. The server stores
 * sha256(fingerprint_b64) per (user_id) row to support multiple devices
 * per account.
 *
 * Why hash on the client: avoid sending the raw signal vector to the
 * server. The server only needs to compare hashes; the raw inputs stay
 * local.
 */

const FP_VERSION = "v1";

function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "no-canvas";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.font = "11pt 'Arial', sans-serif";
    ctx.fillText("Omakase Vault \u{1F510}", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.font = "18pt 'Times New Roman', serif";
    ctx.fillText("Omakase Vault \u{1F510}", 4, 45);
    // Beziér + arcs make GPU/driver differences surface
    ctx.beginPath();
    ctx.arc(50, 30, 18, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = "rgba(255, 0, 255, 0.5)";
    ctx.fill();
    return canvas.toDataURL().slice(-256);
  } catch {
    return "canvas-error";
  }
}

function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return "no-webgl";
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    const version = gl.getParameter(gl.VERSION);
    return `${vendor}|${renderer}|${version}`;
  } catch {
    return "webgl-error";
  }
}

async function getAudioFingerprint(): Promise<string> {
  // Audio fingerprint via OfflineAudioContext. Works in Chrome, Firefox,
  // Safari. Gives a stable sample-rate dependent hash.
  try {
    const Ctx = (window.OfflineAudioContext || (window as unknown as { webkitOfflineAudioContext: typeof OfflineAudioContext }).webkitOfflineAudioContext);
    if (!Ctx) return "no-audio";
    const ctx = new Ctx(1, 44100, 44100);
    const oscillator = ctx.createOscillator();
    oscillator.type = "triangle";
    oscillator.frequency.value = 10000;
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;
    oscillator.connect(compressor);
    compressor.connect(ctx.destination);
    oscillator.start(0);
    const buffer = await ctx.startRendering();
    const chan = buffer.getChannelData(0);
    let sum = 0;
    for (let i = 4500; i < 5000; i++) sum += Math.abs(chan[i] ?? 0);
    return `${ctx.sampleRate}|${sum.toFixed(6)}`;
  } catch {
    return "audio-error";
  }
}

function getScreenFingerprint(): string {
  return [
    screen.width,
    screen.height,
    screen.availWidth,
    screen.availHeight,
    screen.colorDepth,
    screen.pixelDepth,
    window.devicePixelRatio,
  ].join("|");
}

function getNavigatorFingerprint(): string {
  const n = navigator as Navigator & { deviceMemory?: number };
  return [
    navigator.userAgent,
    navigator.platform,
    navigator.hardwareConcurrency ?? 0,
    n.deviceMemory ?? 0,
    (navigator.languages ?? [navigator.language]).join(","),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join("|");
}

/**
 * Compute the SHA-256 hash of the combined fingerprint signals.
 * Returns the hash as base64url so it round-trips cleanly via JSON.
 */
export async function computeDeviceFingerprint(): Promise<string> {
  const parts = [
    `version=${FP_VERSION}`,
    `nav=${getNavigatorFingerprint()}`,
    `screen=${getScreenFingerprint()}`,
    `canvas=${getCanvasFingerprint()}`,
    `webgl=${getWebGLFingerprint()}`,
    `audio=${await getAudioFingerprint()}`,
  ];
  const payload = parts.join("\n");
  const buf = new TextEncoder().encode(payload);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return bytesToBase64Url(new Uint8Array(digest));
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Cache the fingerprint in sessionStorage so we don't recompute on every
// API call within a single tab. Cross-tab + cross-session it's still
// deterministic so the cache is just a perf optimization.
const FP_CACHE_KEY = "ovault_device_fingerprint_v1";

export async function getDeviceFingerprint(): Promise<string> {
  const cached = sessionStorage.getItem(FP_CACHE_KEY);
  if (cached) return cached;
  const fp = await computeDeviceFingerprint();
  sessionStorage.setItem(FP_CACHE_KEY, fp);
  return fp;
}
